/**
 * Estimación de comida con IA: contrato, parseo estricto y borrador editable.
 *
 * Mismo protocolo que la ingesta por imagen de §4.5, y por la misma razón: **lo que devuelve un
 * modelo es una estimación, no una medición.** Nada se guarda sin que el usuario confirme, todo
 * llega precargado y editable, y cada alimento trae su confianza.
 *
 * Y acá la incertidumbre es MAYOR que en la balanza, no menor. "Un plato de arroz con pollo" puede
 * ser 400 kcal o 900 según la porción y el aceite. El diseño lo asume:
 * - el umbral de confianza es más exigente;
 * - se pide al modelo que diga cuando no puede estimar, en vez de promediar a ciegas;
 * - se verifica que los macros expliquen las calorías, que es lo que atrapa un número inventado.
 *
 * Lo que este módulo NO hace: recomendar. No hay objetivos, ni reparto de macros, ni consejos.
 * §10 prohíbe la prescripción de dietas y eso incluye "te falta proteína, comé X".
 */

import type { MomentoDelDia } from './kpis/nutricion.ts';
import { type Hallazgo, type Resultado } from './validacion.ts';

export interface AlimentoEstimado {
  descripcion: string;
  kcal: number | null;
  proteina_g: number | null;
  carbohidratos_g: number | null;
  grasa_g: number | null;
  fibra_g: number | null;
  confianza: number;
}

export interface LecturaComida {
  tipo: 'comida';
  confianza_global: number;
  alimentos: AlimentoEstimado[];
  /** Lo que el modelo no pudo estimar. Decirlo es obligatorio; inventar está prohibido. */
  no_estimables: string[];
}

/**
 * Más exigente que el de la balanza (0.9) porque estimar una porción de comida a partir de una
 * descripción es intrínsecamente más incierto que leer un display.
 */
export const UMBRAL_CONFIANZA_COMIDA = 0.7;

export interface ParseoComida {
  ok: boolean;
  lectura: LecturaComida | null;
  error: string | null;
  veniaEnMarkdown: boolean;
}

function esObjeto(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function aNumeroONull(x: unknown): number | null {
  if (x === null || x === undefined || x === '') return null;
  const n = typeof x === 'string' ? Number(x.replace(',', '.')) : x;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

/** Nunca lanza: un modelo que responde mal es un caso esperado. */
export function parsearComida(texto: string): ParseoComida {
  const bruto = texto.trim();
  if (!bruto) return { ok: false, lectura: null, error: 'Respuesta vacía del modelo.', veniaEnMarkdown: false };

  const fence = bruto.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
  const veniaEnMarkdown = fence !== null;

  let datos: unknown;
  try {
    datos = JSON.parse(fence?.[1] ?? bruto);
  } catch {
    return {
      ok: false,
      lectura: null,
      error: 'El modelo no devolvió JSON válido. No se guarda nada.',
      veniaEnMarkdown,
    };
  }

  if (!esObjeto(datos)) {
    return { ok: false, lectura: null, error: 'El JSON no es un objeto.', veniaEnMarkdown };
  }

  const crudos = datos['alimentos'];
  if (!Array.isArray(crudos)) {
    return { ok: false, lectura: null, error: 'Falta el arreglo de alimentos.', veniaEnMarkdown };
  }

  const alimentos: AlimentoEstimado[] = [];
  for (const item of crudos) {
    if (!esObjeto(item)) continue;
    const descripcion = String(item['descripcion'] ?? '').trim();
    if (!descripcion) continue;

    const confianza = aNumeroONull(item['confianza']);
    alimentos.push({
      descripcion: descripcion.slice(0, 200),
      kcal: aNumeroONull(item['kcal']),
      proteina_g: aNumeroONull(item['proteina_g']),
      carbohidratos_g: aNumeroONull(item['carbohidratos_g']),
      grasa_g: aNumeroONull(item['grasa_g']),
      fibra_g: aNumeroONull(item['fibra_g']),
      // Sin confianza declarada se asume 0, así cae en revisión obligatoria en vez de pasar como
      // dato bueno.
      confianza: confianza === null ? 0 : Math.min(1, Math.max(0, confianza)),
    });
  }

  if (alimentos.length === 0) {
    return {
      ok: false,
      lectura: null,
      error: 'El modelo no identificó ningún alimento. Podés cargarlo a mano.',
      veniaEnMarkdown,
    };
  }

  const noEstimables = Array.isArray(datos['no_estimables'])
    ? (datos['no_estimables'] as unknown[]).filter((x): x is string => typeof x === 'string')
    : [];

  const global = aNumeroONull(datos['confianza_global']);
  const media = alimentos.reduce((a, x) => a + x.confianza, 0) / alimentos.length;

  return {
    ok: true,
    lectura: {
      tipo: 'comida',
      confianza_global: global === null ? media : Math.min(1, Math.max(0, global)),
      alimentos,
      no_estimables: noEstimables,
    },
    error: null,
    veniaEnMarkdown,
  };
}

/** Índices de los alimentos que hay que resaltar para revisión. */
export function alimentosParaRevisar(l: LecturaComida): number[] {
  return l.alimentos
    .map((a, i) => (a.kcal === null || a.proteina_g === null || a.confianza < UMBRAL_CONFIANZA_COMIDA ? i : -1))
    .filter((i) => i >= 0);
}

// ---------------------------------------------------------------------------
// Validación
// ---------------------------------------------------------------------------

export const LIMITES_COMIDA = {
  /** Por alimento. Una pizza entera ronda las 2500; más de eso es un error de estimación. */
  kcal: { min: 0, max: 5000 },
  proteina_g: { min: 0, max: 300 },
  carbohidratos_g: { min: 0, max: 800 },
  grasa_g: { min: 0, max: 400 },
  /**
   * Tolerancia de la identidad calórica: proteína×4 + carbos×4 + grasa×9 debería explicar las
   * kcal. Es el equivalente de "masa magra + masa grasa ≈ peso" de §8, y es lo que atrapa un
   * número inventado: un modelo que alucina macros casi nunca los hace cerrar con las calorías.
   */
  toleranciaCalorica: 0.3,
} as const;

const KCAL_POR_G = { proteina: 4, carbohidratos: 4, grasa: 9 } as const;

export function kcalDeMacros(a: Pick<AlimentoEstimado, 'proteina_g' | 'carbohidratos_g' | 'grasa_g'>): number | null {
  const p = a.proteina_g;
  const c = a.carbohidratos_g;
  const g = a.grasa_g;
  // Sin los tres no se puede verificar nada: no se inventa un cero.
  if (p === null || c === null || g === null) return null;
  return p * KCAL_POR_G.proteina + c * KCAL_POR_G.carbohidratos + g * KCAL_POR_G.grasa;
}

function rango(
  h: Hallazgo[],
  campo: string,
  valor: number | null,
  limites: { min: number; max: number },
  unidad: string,
): void {
  if (valor === null) return;
  if (!Number.isFinite(valor) || valor < limites.min || valor > limites.max) {
    h.push({
      campo,
      severidad: 'error',
      codigo: `${campo}_fuera_de_rango`,
      mensaje: `${valor}${unidad} de ${campo.replace('_g', '')} no es plausible para un alimento (máximo ${limites.max}${unidad}).`,
    });
  }
}

export function validarAlimento(a: {
  descripcion: string;
  kcal: number | null;
  proteina_g: number | null;
  carbohidratos_g: number | null;
  grasa_g: number | null;
}): Resultado {
  const h: Hallazgo[] = [];

  if (!a.descripcion.trim()) {
    h.push({
      campo: 'descripcion',
      severidad: 'error',
      codigo: 'sin_descripcion',
      mensaje: 'Falta decir qué comiste.',
    });
  }

  if (a.kcal === null) {
    h.push({
      campo: 'kcal',
      severidad: 'error',
      codigo: 'sin_kcal',
      mensaje: 'Faltan las calorías.',
    });
  }
  if (a.proteina_g === null) {
    h.push({
      campo: 'proteina_g',
      severidad: 'error',
      codigo: 'sin_proteina',
      mensaje: 'Falta la proteína, que es el dato que le importa a esta app.',
    });
  }

  rango(h, 'kcal', a.kcal, LIMITES_COMIDA.kcal, ' kcal');
  rango(h, 'proteina_g', a.proteina_g, LIMITES_COMIDA.proteina_g, ' g');
  rango(h, 'carbohidratos_g', a.carbohidratos_g, LIMITES_COMIDA.carbohidratos_g, ' g');
  rango(h, 'grasa_g', a.grasa_g, LIMITES_COMIDA.grasa_g, ' g');

  // La proteína sola no puede aportar más calorías de las que tiene el alimento.
  if (a.kcal !== null && a.proteina_g !== null && a.proteina_g * KCAL_POR_G.proteina > a.kcal * 1.15) {
    h.push({
      campo: 'proteina_g',
      severidad: 'error',
      codigo: 'proteina_supera_kcal',
      mensaje: `${a.proteina_g} g de proteína son ${Math.round(a.proteina_g * 4)} kcal, más de las ${a.kcal} kcal del alimento. Algún número está mal.`,
    });
  }

  const deMacros = kcalDeMacros(a);
  if (a.kcal !== null && deMacros !== null && a.kcal > 0 && deMacros > 0) {
    const desvio = Math.abs(deMacros - a.kcal) / a.kcal;
    if (desvio > LIMITES_COMIDA.toleranciaCalorica) {
      h.push({
        campo: 'kcal',
        severidad: 'confirmar',
        codigo: 'macros_no_cierran',
        mensaje: `Los macros dan ${Math.round(deMacros)} kcal pero dice ${Math.round(a.kcal)}. Puede ser una estimación floja; revisá antes de guardar.`,
      });
    }
  }

  const errores = h.filter((x) => x.severidad === 'error');
  return {
    ok: errores.length === 0,
    requiereConfirmacion: h.some((x) => x.severidad === 'confirmar'),
    hallazgos: h,
  };
}

// ---------------------------------------------------------------------------
// Borrador editable
// ---------------------------------------------------------------------------

export interface BorradorComida {
  momento: MomentoDelDia;
  alimentos: AlimentoEstimado[];
  paraRevisar: number[];
  noEstimables: string[];
  confianzaGlobal: number;
}

export function aBorradorComida(l: LecturaComida, momento: MomentoDelDia): BorradorComida {
  return {
    momento,
    alimentos: l.alimentos,
    paraRevisar: alimentosParaRevisar(l),
    noEstimables: l.no_estimables,
    confianzaGlobal: l.confianza_global,
  };
}

export function totalesDelBorrador(b: BorradorComida): { kcal: number; proteina_g: number } {
  return b.alimentos.reduce(
    (acc, a) => ({ kcal: acc.kcal + (a.kcal ?? 0), proteina_g: acc.proteina_g + (a.proteina_g ?? 0) }),
    { kcal: 0, proteina_g: 0 },
  );
}

/** El momento del día que corresponde a una hora. Sugerencia, el usuario lo cambia. */
export function momentoPorHora(hora: string): MomentoDelDia {
  const h = Number(hora.slice(0, 2));
  if (!Number.isFinite(h)) return 'snack';
  if (h < 11) return 'desayuno';
  if (h < 16) return 'almuerzo';
  if (h < 22) return 'cena';
  return 'snack';
}

// ---------------------------------------------------------------------------
// Prompt
// ---------------------------------------------------------------------------

export const PROMPT_COMIDA = `
Estimás el contenido nutricional de lo que una persona describe que comió.

Devolvés EXCLUSIVAMENTE un objeto JSON. Sin prosa, sin explicaciones, sin markdown, sin \`\`\`.

Forma exacta:
{"tipo":"comida","confianza_global":0.0,"alimentos":[{"descripcion":"","kcal":0,"proteina_g":0,"carbohidratos_g":0,"grasa_g":0,"fibra_g":0,"confianza":0.0}],"no_estimables":[]}

Reglas que no se negocian:
- Un elemento del arreglo por cada alimento distinto. No juntes todo en uno: la persona necesita
  poder corregir un alimento sin tocar los demás.
- En "descripcion" repetí el alimento CON la cantidad que asumiste, explícita: "150 g de pechuga
  de pollo", "1 taza de arroz cocido (185 g)". Si la persona no dijo cantidad, asumí una porción
  típica y decilo en la descripción. Nunca escondas el supuesto.
- "confianza" es tu certeza real, de 0 a 1. Si la persona dijo el peso exacto de un alimento
  simple, puede ser 0.9. Si dijo "un plato de tallarines", no pasa de 0.5: la porción y el aceite
  cambian el total al doble. NO infles la confianza.
- Los macros tienen que CERRAR con las calorías: proteina_g×4 + carbohidratos_g×4 + grasa_g×9 debe
  aproximarse a kcal. Si no cierran, revisá tus números antes de responder.
- Si algo no se puede estimar —"una salsa", "un postre" sin más datos— ponelo en "no_estimables"
  con lo que falta saber, y NO lo incluyas en "alimentos". Un número inventado es peor que un
  faltante: el faltante se le pregunta a la persona, el inventado se guarda como si fuera verdad.
- No des consejos, ni objetivos, ni opiniones sobre la comida. Sólo estimás.
- Todo en gramos y kilocalorías. Español.
`.trim();

export const PROMPT_COMIDA_FOTO = `
${PROMPT_COMIDA}

La entrada es una FOTO de un plato. Además de lo anterior:
- Estimá la porción por referencias visibles (tamaño del plato, cubiertos, envases). Decí en la
  descripción qué usaste como referencia.
- Sin referencia de escala, bajá la confianza a 0.4 o menos: una foto sin escala no permite
  estimar una porción con seriedad.
- Si hay una etiqueta nutricional visible, LEELA en vez de estimar, y subí la confianza.
`.trim();
